import React from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { Code2, Database, Layout, Award, Briefcase, GraduationCap } from 'lucide-react';

const About = () => {
  const { portfolioData, loading } = usePortfolio();
  
  if (loading || !portfolioData) {
    return null;
  }
  
  const { basics, education } = portfolioData;

  const highlights = [
    { icon: Briefcase, text: '6+ Years Experience', color: 'text-orange-500' },
    { icon: Code2, text: 'Full Stack Expertise', color: 'text-amber-500' },
    { icon: Layout, text: 'Modern Frameworks', color: 'text-orange-400' },
    { icon: Award, text: 'Award Winner', color: 'text-orange-600' }
  ];

  return (
    <section id="about" data-story-section className="py-32 bg-zinc-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,107,53,0.05),transparent_50%),radial-gradient(circle_at_70%_50%,rgba(251,146,60,0.05),transparent_50%)]"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-orange-500 uppercase tracking-wider">About Me</h2>
              <h3 className="text-5xl md:text-6xl font-bold text-white leading-tight">
                Building Digital
                <span className="gradient-text"> Excellence</span>
              </h3>
            </div>

            <p className="text-lg text-gray-400 leading-relaxed">
              {basics.summary}
            </p>

            <div className="grid grid-cols-2 gap-6 pt-6">
              {highlights.map((item, index) => (
                <div key={index} className="glass p-4 rounded-xl border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 group">
                  <div className={`${item.color} mb-2 group-hover:scale-110 transition-transform`}>
                    <item.icon size={28} />
                  </div>
                  <p className="font-semibold text-white text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Education */}
          <div className="space-y-6">
            <div className="relative">
              {/* Decorative glow */}
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
              
              <div className="space-y-6 relative">
                <div className="flex items-center gap-3 mb-8">
                  <GraduationCap size={32} className="text-orange-500" />
                  <h4 className="text-3xl font-bold text-white">Education</h4>
                </div>
                
                {education.map((edu, index) => (
                  <div key={index} className="glass p-8 rounded-2xl border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 hover:translate-x-2 group">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h5 className="text-2xl font-bold text-white mb-1 group-hover:text-orange-500 transition-colors">{edu.studyType}</h5>
                        <p className="text-orange-500 font-semibold text-lg">{edu.area}</p>
                      </div>
                    </div>
                    <p className="text-gray-300 font-medium text-lg mb-1">{edu.institution}</p>
                    <p className="text-gray-500 text-sm mb-3">{edu.location}</p>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-orange-500/10 text-orange-500 text-xs font-medium border border-orange-500/20">
                        {new Date(edu.startDate).getFullYear()} - {new Date(edu.endDate).getFullYear()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Decorative shapes */}
              <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-amber-500/5 rotate-45 animate-float"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
